// ─── La escalera de ampliación de un deep link `?inspection=<id>` ────────────
//
// Con el historial acotado al día en curso, un enlace a una inspección vieja
// apunta a algo que no está en el conjunto cargado. Como el módulo no expone un
// `GET /yardInspection?id=`, la única vía es ampliar el rango — pero a una
// ventana ACOTADA, nunca a "sin límites": dejar las fechas vacías pone a la
// pantalla a barrer 20x500 documentos con una subconsulta de `evidences` por
// cada uno, cada 5 s, durante el resto de la sesión.
//
// Esto vive fuera del componente porque la secuencia tiene un orden que hay que
// poder fijar con tests. El bug que motivó extraerlo es exactamente de orden:
// la versión anterior comparaba contra el array del store de zustand, que lo
// escribe un efecto SEPARADO del que decide el paso. Los dos corren en el mismo
// commit y en orden de declaración, así que en el commit en que llegan los
// datos del rango ampliado `dataUpdatedAt` ya es nuevo pero el array del store
// todavía es el del rango ANTERIOR. Resultado medido en el test de abajo: el
// peldaño de 30 días no se honraba nunca, y una inspección de entre 30 y 365
// días jamás abría su modal — se agotaba la escalera comparando siempre contra
// el conjunto viejo, se limpiaba el param, y el commit siguiente traía los
// datos correctos cuando ya nadie los estaba mirando. De yapa quedaba
// `dateFrom` a 365 días atrás, o sea el barrido que todo esto vino a eliminar.
//
// Por eso `loaded` tiene que ser el MISMO objeto cuya llegada reporta
// `dataUpdatedAt`, no una copia que viaje por otro camino.

/** Peldaños en días. Acotados a propósito; nunca incluye "sin límites". */
export const DEEP_LINK_WIDEN_DAYS = [30, 365] as const;

export interface DeepLinkState {
  /** Id para el que ya se empezó la escalera, o null si no se empezó. */
  widenedFor: string | null;
  /** Índice del peldaño aplicado. */
  widenStep: number;
  /**
   * La clave de rango que dejó puesta ese peldaño (el mismo string que la
   * pantalla calcula para `loadedKey`), o null si todavía no se amplió.
   */
  widenedKey: string | null;
}

export interface DeepLinkInput<T extends { id: string }> extends DeepLinkState {
  requestedId: string;
  /**
   * El conjunto que corresponde a `dataUpdatedAt`. Si acá llega una copia
   * atrasada, la escalera se salta peldaños — ver el comentario de arriba.
   */
  loaded: readonly T[] | undefined;
  /** `dataUpdatedAt` de react-query: cuándo se escribió `loaded`. */
  dataUpdatedAt: number;
  /**
   * Qué rango es el que describe `loaded`. Sale de los mismos `dateFrom` /
   * `dateTo` que arman la query-key, así que cambia exactamente cuando cambia
   * el conjunto pedido. Ver el gate de abajo para por qué no alcanza el reloj.
   */
  loadedKey: string;
}

export type DeepLinkAction<T> =
  | { type: "open"; inspection: T }
  /** Aplicar este rango y esperar. `step` va de vuelta al estado. */
  | { type: "widen"; days: number; step: number }
  /** Se agotó la escalera: limpiar el param sin abrir nada. */
  | { type: "giveUp" }
  /** Todavía no llegó la respuesta del peldaño vigente. */
  | { type: "wait" };

/**
 * Decide el próximo paso. Pura: no toca refs, estado ni la URL.
 */
export function deepLinkStep<T extends { id: string }>(
  input: DeepLinkInput<T>,
): DeepLinkAction<T> {
  const {
    requestedId,
    loaded,
    dataUpdatedAt,
    loadedKey,
    widenedFor,
    widenStep,
    widenedKey,
  } = input;

  // Todavía no hay respuesta para la ventana vigente. Es distinto de "llegó y
  // no está", y confundirlos era un bug: al montar, `inspectionsQuery.data` es
  // `undefined` (query-key nueva, sin placeholderData) y el efecto corre en el
  // primer commit, así que `loaded?.find()` daba `undefined` y la escalera
  // arrancaba de una. Entrar a `?inspection=<id>` de una inspección DE HOY
  // abandonaba la consulta del día antes de que contestara y dejaba la pantalla
  // en 30 días con `refetchInterval: 5000` por el resto de la sesión — el
  // barrido que todo esto vino a eliminar.
  //
  // `dataUpdatedAt === 0` es lo que react-query reporta mientras una key nunca
  // resolvió con éxito; se chequea junto con `loaded` porque una key nueva pasa
  // por los dos estados.
  if (loaded === undefined || dataUpdatedAt === 0) return { type: "wait" };

  const match = loaded.find((i) => i.id === requestedId);
  if (match) return { type: "open", inspection: match };

  // Primer intento para este id: arrancar la escalera.
  if (widenedFor !== requestedId) {
    return { type: "widen", days: DEEP_LINK_WIDEN_DAYS[0], step: 0 };
  }

  // Ya se amplió: no se avanza hasta tener el conjunto DEL PELDAÑO VIGENTE.
  // Sin este gate se atravesaría la escalera entera en los renders que ocurren
  // mientras el fetch está en vuelo.
  //
  // Se compara la CLAVE del rango, no el reloj. Antes acá decía
  // `dataUpdatedAt <= widenedAt`, que asume que ampliar siempre produce un
  // fetch fresco — y no es cierto: `queryClient.ts` corre con
  // `staleTime: 30_000` y `gcTime: 5 * 60_000`, así que en el segundo deep link
  // de una sesión la key de 30 días suele seguir cacheada y FRESCA. react-query
  // entrega el conjunto cacheado sin refetchear al montar, `dataUpdatedAt`
  // queda con la marca vieja —anterior a `widenedAt`— y el peldaño se trababa
  // en "wait" hasta que disparara el `refetchInterval`, que con el rango
  // ampliado es de 30 s y no de 5 s (REFETCH_WIDE_MS). Peor caso que sale de
  // esos dos números: 30 s por peldaño, o sea ~60 s de pantalla aparentemente
  // vacía para resolver una escalera de dos peldaños — justo la latencia que el
  // deep link viene a evitar.
  //
  // La clave no tiene ese problema porque no habla de frescura sino de
  // identidad: mientras `loaded` sea el conjunto del rango anterior la clave no
  // coincide (react-query deja `data` en undefined al cambiar de key sin caché,
  // y eso ya sale por el `wait` de arriba), y en cuanto es el del peldaño
  // vigente coincide, venga de la red o del caché. Servir un conjunto cacheado
  // de hasta 30 s es aceptable acá: lo que se busca es una inspección cuya
  // entrada es de días o meses atrás, no una fila que acabe de aparecer.
  if (widenedKey !== null && loadedKey !== widenedKey) return { type: "wait" };

  const nextStep = widenStep + 1;
  if (nextStep < DEEP_LINK_WIDEN_DAYS.length) {
    return { type: "widen", days: DEEP_LINK_WIDEN_DAYS[nextStep], step: nextStep };
  }

  // Se agotó: el id no existe, es de otra empresa, o cae fuera de los patios
  // asignados. Se limpia el param igual, porque dejarlo puesto hace que cada
  // render reintente para siempre.
  return { type: "giveUp" };
}
